"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { TRADE_PAGES } from "@/lib/marketing/trade-pages";

const TRADES = TRADE_PAGES.map((t) => ({
  id: t.slug,
  label: t.label,
  blurb: t.howItRuns,
}));

type TradeId = (typeof TRADES)[number]["id"];

export function TradeFitSection() {
  const [active, setActive] = useState<TradeId>("general-contractor");
  const copy = useMemo(() => TRADES.find((t) => t.id === active)?.blurb ?? "", [active]);

  return (
    <section className="border-b border-slate-200/80 bg-slate-50 py-10 sm:py-12" id="trade-fit">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
          Built for service businesses
        </p>
        <h2 className="mt-2 text-center font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          How teams like yours use Jorvio
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-slate-600 sm:text-base">
          Pick the kind of work you do for a plain-language example. Same Jorvio for everyone; the story below shows how
          that workflow usually runs for teams like yours.
        </p>
        <div className="mx-auto mt-6 flex max-w-4xl flex-nowrap justify-start gap-2 overflow-x-auto overflow-y-hidden pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap sm:justify-center sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden">
          {TRADES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t.id)}
              className={cn(
                "shrink-0 touch-manipulation rounded-full border px-4 py-2.5 text-sm font-medium transition min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                active === t.id
                  ? "border-primary/80 bg-primary text-primary-foreground shadow-md"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-2xl text-pretty text-center text-base leading-relaxed text-slate-700 sm:text-lg">
          {copy}
        </p>
        <p className="mx-auto mt-6 max-w-xl text-center text-xs text-slate-500">
          Ready to try it?{" "}
          <Link href="/signup" className="font-semibold text-primary underline-offset-4 hover:underline">
            Create a free account
          </Link>
          {" · "}
          <Link href={`/for/${active}`} className="font-semibold text-primary underline-offset-4 hover:underline">
            {TRADES.find((t) => t.id === active)?.label ?? "Trade"} page
          </Link>
          . No credit card required to get started.
        </p>
      </div>
    </section>
  );
}
